import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X, AlertTriangle, Sparkles, RotateCcw, FileText } from "lucide-react";

// Voice rules — kept in sync with /voice-prompt SYSTEM_PROMPT
const ALWAYS_WORDS = [
  { word: "HYGIENE", weight: 10, why: "Brand spine — should appear in most wellness posts" },
  { word: "I GET TO", weight: 10, why: "Brand thesis — the entire reframe philosophy" },
  { word: "Tapped-In", weight: 6, why: "The 60-second daily practice" },
  { word: "Divine Team", weight: 5, why: "Spiritual hygiene anchor" },
  { word: "Personal Code of Conduct", weight: 4, why: "Your decision-making framework" },
  { word: "Yes. No. Wait.", weight: 4, why: "Your decision cards" },
  { word: "messy middle", weight: 5, why: "Audience identifier" },
  { word: "real tools", weight: 4, why: "Brand promise" },
  { word: "Compassionate Curiosity", weight: 3, why: "The CC's philosophy" },
];

const NEVER_WORDS = [
  { word: "manifest", weight: 8, alt: '"call in" or "create"' },
  { word: "queen", weight: 8, alt: '"dear one" or "Lovely"' },
  { word: "sis", weight: 7, alt: '"Lovely" or remove' },
  { word: "babe", weight: 6, alt: 'Only "Temple, sillybean" — never readers' },
  { word: "bestie", weight: 7, alt: '"dear one"' },
  { word: "good vibes", weight: 10, alt: "This is toxic positivity — DELETE" },
  { word: "high vibe", weight: 8, alt: '"Tapped-In"' },
  { word: "raise your vibration", weight: 8, alt: '"Tap In"' },
  { word: "everything happens for a reason", weight: 10, alt: "Toxic positivity — DELETE" },
  { word: "stay positive", weight: 9, alt: 'Toxic positivity — say "what do you need?" instead' },
  { word: "trust the journey", weight: 7, alt: "Cliché — replace with specifics" },
  { word: "hustle", weight: 7, alt: '"intentional work"' },
  { word: "boss babe", weight: 9, alt: "Hard NO — DELETE" },
  { word: "self-care sunday", weight: 6, alt: '"daily HYGIENE"' },
  { word: "you got this", weight: 4, alt: '"You GET TO"' },
  { word: "namaste", weight: 5, alt: 'Replace with "💛" or "Welcome home"' },
  { word: "let's gooo", weight: 7, alt: "Hard NO" },
  { word: "crush it", weight: 7, alt: '"Live it"' },
];

const SIGNOFFS = ["— Temple", "Temple 💛", "Welcome home", "💛"];

const PLATFORMS = [
  { id: "ig", label: "Instagram (≤2,200 chars)", min: 150, max: 2200 },
  { id: "fb", label: "Facebook (long-form OK)", min: 200, max: 8000 },
  { id: "twitter", label: "Twitter/X (≤280 chars)", min: 50, max: 280 },
  { id: "pin", label: "Pinterest description (≤500 chars)", min: 100, max: 500 },
  { id: "email", label: "Email subject line (≤50 chars)", min: 10, max: 50 },
  { id: "blog", label: "Blog/long-form essay (no limit)", min: 300, max: 50000 },
];

const SAMPLE = `Setting a boundary with family is the hardest kind. They installed your buttons — they know how to push them.

But here's the part nobody teaches us: a boundary is not a wall. It's a bridge with a guardrail.

This week, when "after everything I've done for you" lands like a gut punch — try this:

"I'm grateful for everything you've done. AND I need to take care of myself too."

Both can be true. That AND is the whole HYGIENE.

You GET TO love your family AND honor yourself. That's not a contradiction. That's adulthood.

— Temple 💛

#mindbodyspirithygiene #boundaries #igetto #templefranklin`;

const STORAGE_KEY = "temple_cowork_test_v1";

const checkText = (text, platform) => {
  if (!text || text.trim().length < 5) return null;
  const lower = text.toLowerCase();

  // ALWAYS check (case-insensitive substring match)
  const alwaysFound = [];
  const alwaysMissing = [];
  ALWAYS_WORDS.forEach((rule) => {
    if (lower.includes(rule.word.toLowerCase())) {
      alwaysFound.push(rule);
    } else {
      alwaysMissing.push(rule);
    }
  });

  // NEVER check
  const neverFound = [];
  NEVER_WORDS.forEach((rule) => {
    if (lower.includes(rule.word.toLowerCase())) {
      neverFound.push(rule);
    }
  });

  // Sign-off check
  const hasSignoff = SIGNOFFS.some((s) => text.includes(s));

  // Length check
  const len = text.length;
  const lenOk = len >= platform.min && len <= platform.max;

  // Tone signals (heuristic)
  const hasOneReader = /\byou\b/i.test(text); // speaks to "you" (one reader)
  const hasCrowdSignal = /\b(everyone|everybody|all of you|you guys)\b/i.test(text);
  const excessiveEmojis = (text.match(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu) || []).length > 5;
  const hasAllCaps = /[A-Z]{4,}/.test(text); // intentional emphasis like "I GET TO"

  // SCORING
  let score = 100;
  const reasons = [];

  // Penalty: never-words found
  neverFound.forEach((r) => {
    score -= r.weight * 1.5;
    reasons.push({ type: "fail", text: `Used "${r.word}" — ${r.alt}` });
  });

  // Penalty: missing key always-words (only penalize the top 4)
  const keyAlways = ALWAYS_WORDS.filter((w) => w.weight >= 5);
  const missingKey = keyAlways.filter((w) => !alwaysFound.some((f) => f.word === w.word));
  if (missingKey.length === keyAlways.length) {
    score -= 20;
    reasons.push({ type: "fail", text: `No brand language found (HYGIENE / I GET TO / Tapped-In / etc.)` });
  } else if (missingKey.length > 2) {
    score -= 8;
    reasons.push({ type: "warn", text: `Only ${keyAlways.length - missingKey.length}/${keyAlways.length} brand anchors used — try adding HYGIENE or I GET TO` });
  }

  // Penalty: no sign-off (skip for short formats)
  if (platform.id !== "twitter" && platform.id !== "email" && platform.id !== "pin" && !hasSignoff) {
    score -= 10;
    reasons.push({ type: "warn", text: `No sign-off (— Temple 💛 or Welcome home)` });
  }

  // Penalty: length out of bounds
  if (!lenOk) {
    score -= 8;
    if (len < platform.min) {
      reasons.push({ type: "warn", text: `Too short for ${platform.label} (${len} / min ${platform.min})` });
    } else {
      reasons.push({ type: "fail", text: `Too long for ${platform.label} (${len} / max ${platform.max})` });
    }
  }

  // Penalty: addresses crowd not one reader
  if (hasCrowdSignal) {
    score -= 6;
    reasons.push({ type: "warn", text: `Addresses a crowd ("everyone" / "you guys") — speak to ONE woman` });
  }

  // Penalty: emoji overload
  if (excessiveEmojis) {
    score -= 5;
    reasons.push({ type: "warn", text: `Too many emojis — Temple uses 💛 sparingly` });
  }

  // Bonus: has "you" + has emphasis caps + has signoff = pure Temple
  if (hasOneReader && hasAllCaps && hasSignoff) {
    score += 3;
    reasons.push({ type: "pass", text: `One-reader voice + intentional emphasis + sign-off = ✨` });
  }

  if (alwaysFound.length >= 3) {
    reasons.push({ type: "pass", text: `${alwaysFound.length} brand anchors used 💛` });
  }

  if (neverFound.length === 0) {
    reasons.push({ type: "pass", text: `Zero forbidden words — clean voice` });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  let grade = "A+";
  let gradeColor = "bg-[#25a1c2] text-white";
  let gradeLabel = "Pure Temple voice ✨";
  if (score < 95) {
    grade = "A";
    gradeLabel = "Strong voice match — ship it";
  }
  if (score < 85) {
    grade = "B";
    gradeColor = "bg-[#5EEAD4] text-[#042F2E]";
    gradeLabel = "Close, but a few tweaks would sharpen it";
  }
  if (score < 75) {
    grade = "C";
    gradeColor = "bg-amber-400 text-amber-900";
    gradeLabel = "Partially on-brand — rewrite key sections";
  }
  if (score < 60) {
    grade = "D";
    gradeColor = "bg-orange-400 text-white";
    gradeLabel = "Off-brand — start over";
  }
  if (score < 45) {
    grade = "F";
    gradeColor = "bg-red-500 text-white";
    gradeLabel = "AI slop — train the skill more";
  }

  return {
    score,
    grade,
    gradeColor,
    gradeLabel,
    alwaysFound,
    alwaysMissing,
    neverFound,
    hasSignoff,
    len,
    lenOk,
    reasons,
  };
};

export default function CoworkTest() {
  const [text, setText] = useState("");
  const [platform, setPlatform] = useState(PLATFORMS[0]);

  useEffect(() => {
    document.title = "Cowork Tester — Grade Your AI Outputs | Temple Franklin";
    const robotsTag = document.querySelector('meta[name="robots"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      document.head.appendChild(m);
      return m;
    })();
    const prevRobots = robotsTag.getAttribute("content");
    robotsTag.setAttribute("content", "noindex, nofollow");
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setText(saved);
    } catch (e) {
      // ignore
    }
    return () => {
      if (prevRobots !== null) robotsTag.setAttribute("content", prevRobots);
      else robotsTag.removeAttribute("content");
    };
  }, []);

  useEffect(() => {
    try {
      if (text) localStorage.setItem(STORAGE_KEY, text);
    } catch (e) {
      // ignore
    }
  }, [text]);

  const result = useMemo(() => checkText(text, platform), [text, platform]);

  const reset = () => {
    setText("");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="cowork-test-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/social"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Voice Tester · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-5xl mx-auto">
          {/* HERO */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#25a1c2] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3 h-3" /> Quality Check Before You Post
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-4">
              The Voice Tester
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Paste any AI output → instant grade against your voice rules. Keeps you safe from "AI slop" before it goes live.
            </p>
          </div>

          {/* INPUT + GRADE — 2 col on desktop */}
          <div className="grid lg:grid-cols-5 gap-6 mb-10">
            {/* INPUT */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                  <h2 className="font-serif text-xl text-[#042F2E]">Paste AI Output Here</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setText(SAMPLE)}
                      className="inline-flex items-center gap-1.5 bg-[#F0F5F4] hover:bg-[#CCFBF1] text-[#25a1c2] text-xs font-bold rounded-full px-3 py-1.5 transition-all"
                      data-testid="load-sample-btn"
                    >
                      <FileText className="w-3 h-3" /> Load sample
                    </button>
                    {text && (
                      <button
                        onClick={reset}
                        className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#042F2E] transition-all"
                        data-testid="reset-btn"
                      >
                        <RotateCcw className="w-3 h-3" /> Clear
                      </button>
                    )}
                  </div>
                </div>

                <select
                  value={platform.id}
                  onChange={(e) => setPlatform(PLATFORMS.find((p) => p.id === e.target.value))}
                  className="w-full mb-3 bg-[#efeae7] border border-[#CCFBF1] rounded-xl px-4 py-2.5 text-sm text-[#042F2E] focus:outline-none focus:border-[#25a1c2]"
                  data-testid="platform-select"
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>

                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste the post Cowork or any AI just wrote you..."
                  className="w-full h-72 bg-[#efeae7] border border-[#CCFBF1] rounded-xl px-4 py-3 text-sm text-[#042F2E] focus:outline-none focus:border-[#25a1c2] resize-none font-mono"
                  data-testid="text-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-[#475569]">{text.length.toLocaleString()} chars</span>
                  <span className={`text-xs font-bold ${text.length >= platform.min && text.length <= platform.max ? "text-[#25a1c2]" : "text-amber-600"}`}>
                    {platform.id !== "blog" && `${platform.min}–${platform.max} for ${platform.label.split(" ")[0]}`}
                  </span>
                </div>
              </div>
            </div>

            {/* GRADE */}
            <div className="lg:col-span-2">
              {!result ? (
                <div className="bg-white rounded-2xl border-2 border-dashed border-[#CCFBF1] p-8 text-center h-full flex flex-col items-center justify-center" data-testid="grade-empty">
                  <Sparkles className="w-10 h-10 text-[#25a1c2]/30 mb-3" />
                  <p className="font-serif text-lg text-[#475569] mb-1">Awaiting your text</p>
                  <p className="text-xs text-[#475569]">Paste any AI output → I'll grade it instantly</p>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-[#042F2E] to-[#068fa2] rounded-2xl p-6 text-white" data-testid="grade-result">
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-2">
                    Your Voice Match Score
                  </p>
                  <div className="flex items-baseline gap-3 mb-3">
                    <p className="font-serif text-7xl text-white leading-none" data-testid="score-number">
                      {result.score}
                    </p>
                    <span className={`${result.gradeColor} text-2xl font-serif font-bold rounded-xl px-3 py-1`} data-testid="grade-letter">
                      {result.grade}
                    </span>
                  </div>
                  <p className="text-[#5EEAD4] text-sm font-bold mb-4">{result.gradeLabel}</p>

                  <div className="w-full bg-white/10 rounded-full h-2 mb-5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#5EEAD4] to-[#CCFBF1] h-full transition-all duration-700 ease-out"
                      style={{ width: `${result.score}%` }}
                    ></div>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    <p>
                      <strong className="text-[#5EEAD4]">{result.alwaysFound.length}</strong>
                      <span className="text-white/70"> brand anchors found</span>
                    </p>
                    <p>
                      <strong className={result.neverFound.length === 0 ? "text-[#5EEAD4]" : "text-red-300"}>
                        {result.neverFound.length}
                      </strong>
                      <span className="text-white/70"> forbidden words</span>
                    </p>
                    <p>
                      <strong className={result.hasSignoff ? "text-[#5EEAD4]" : "text-amber-300"}>
                        {result.hasSignoff ? "✓" : "✗"}
                      </strong>
                      <span className="text-white/70"> sign-off present</span>
                    </p>
                    <p>
                      <strong className={result.lenOk ? "text-[#5EEAD4]" : "text-amber-300"}>
                        {result.len.toLocaleString()}
                      </strong>
                      <span className="text-white/70"> chars · {result.lenOk ? "in range" : "out of range"}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* DETAILED FEEDBACK */}
          {result && (
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Reasons / Action items */}
              <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6" data-testid="feedback-card">
                <h3 className="font-serif text-xl text-[#042F2E] mb-4">What to fix · what to keep</h3>
                {result.reasons.length === 0 ? (
                  <p className="text-[#475569] text-sm italic">Type something to get feedback.</p>
                ) : (
                  <div className="space-y-2.5">
                    {result.reasons.map((r, i) => {
                      const icon = r.type === "pass" ? Check : r.type === "warn" ? AlertTriangle : X;
                      const Icon = icon;
                      const cls =
                        r.type === "pass"
                          ? "bg-[#F0FDF4] border-[#25a1c2]/30 text-[#042F2E]"
                          : r.type === "warn"
                          ? "bg-amber-50 border-amber-200 text-amber-900"
                          : "bg-red-50 border-red-200 text-red-900";
                      const iconCls =
                        r.type === "pass" ? "text-[#25a1c2]" : r.type === "warn" ? "text-amber-600" : "text-red-600";
                      return (
                        <div
                          key={i}
                          className={`flex items-start gap-2.5 p-3 rounded-xl border ${cls}`}
                          data-testid={`reason-${i}`}
                        >
                          <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${iconCls}`} />
                          <p className="text-xs leading-relaxed flex-1">{r.text}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Forbidden words found */}
              <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6" data-testid="forbidden-card">
                <h3 className="font-serif text-xl text-[#042F2E] mb-4">
                  {result.neverFound.length === 0 ? "🎉 No forbidden words" : `🚨 ${result.neverFound.length} forbidden word${result.neverFound.length > 1 ? "s" : ""}`}
                </h3>
                {result.neverFound.length === 0 ? (
                  <div className="bg-[#F0FDF4] border border-[#25a1c2]/30 rounded-xl p-4 text-center">
                    <Check className="w-8 h-8 text-[#25a1c2] mx-auto mb-2" />
                    <p className="text-sm text-[#042F2E] font-bold">Clean. Pure Temple voice on the surface.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {result.neverFound.map((r, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-red-900">"{r.word}"</p>
                        <p className="text-[11px] text-red-700 italic mt-0.5">→ Try: {r.alt}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HOW IT WORKS */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10" data-testid="how-it-works">
            <p className="font-bold text-amber-900 mb-3">📋 How to use this tester:</p>
            <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside">
              <li>Get a draft from Cowork, ChatGPT, Claude, or any AI</li>
              <li>Pick the right platform from the dropdown (Instagram, Facebook, etc.)</li>
              <li>Paste the draft into the box</li>
              <li>Read the score + feedback. <strong>A or A+</strong> = ship it. <strong>B or below</strong> = tell the AI <em>"this isn't my voice — fix [issue]"</em></li>
              <li>Re-paste once it improves. Repeat until A+. ✨</li>
            </ol>
            <p className="text-amber-800 text-xs italic mt-3">
              Note: This is a heuristic check, not a perfect critic. A 78 might still be perfect for the moment. Use it as a guide, not a gospel.
            </p>
          </div>

          {/* RELATED LINKS */}
          <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-4">
              Related Tools
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <Link to="/voice-prompt" className="flex items-center gap-2 p-3 rounded-xl bg-[#efeae7] hover:bg-[#F0F5F4] text-[#042F2E] text-sm transition-all">
                <span className="text-xs">💬 Voice Prompt</span>
              </Link>
              <Link to="/voice-pack" className="flex items-center gap-2 p-3 rounded-xl bg-[#efeae7] hover:bg-[#F0F5F4] text-[#042F2E] text-sm transition-all">
                <span className="text-xs">🧠 Voice Pack</span>
              </Link>
              <Link to="/content-bank" className="flex items-center gap-2 p-3 rounded-xl bg-[#efeae7] hover:bg-[#F0F5F4] text-[#042F2E] text-sm transition-all">
                <span className="text-xs">🪨 Content Bank</span>
              </Link>
            </div>
          </div>

          <p className="text-center text-[10px] text-[#475569] italic mt-8">
            Bookmark · mindbodyspirithygienetools.com/cowork-test
          </p>
        </div>
      </div>
    </div>
  );
}
